import { resolveEnemy } from '../../content/enemies/enemies';
import { buildCombatFromEnemy } from '../../domain/combat/buildCombat';
import { resolvePreCombatFlee } from '../../domain/combat/flee';
import { generateFloor } from '../../domain/dungeon/generateFloor';
import { getCurrentRoom } from '../../domain/dungeon/roomRules';
import { moveByDirection, moveToRoomId } from '../../domain/dungeon/move';
import { applyConsequence } from '../../domain/events/applyConsequence';
import { modeFromRoom } from '../../domain/game/modeFromRoom';
import { gameSignals } from '../../game/events/gameSignals';
import { enemyKeyFromOption, MOVE_FAIL_MESSAGE, retreatToPrevious, successfulMovePatch } from '../helpers';
import type { GameActions, GameStoreGet, GameStoreSet } from '../types';

export function createExplorationActions(
  set: GameStoreSet,
  get: GameStoreGet,
): Pick<
  GameActions,
  'moveToRoom' | 'moveByDir' | 'selectOption' | 'returnToExploration' | 'advanceFloor'
> {
  return {
    moveToRoom: (roomId) => {
      const { dungeon, player } = get();
      const result = moveToRoomId(dungeon, roomId, player.alive);
      if (!result.ok) {
        set({ lastMessage: MOVE_FAIL_MESSAGE[result.reason] ?? 'Không thể di chuyển.' });
        return;
      }
      set(successfulMovePatch(dungeon.playerRoomId, result.floor));
      gameSignals.emit({ type: 'room-transition', roomId: result.floor.playerRoomId });
    },

    moveByDir: (direction) => {
      const { dungeon, player } = get();
      const result = moveByDirection(dungeon, direction, player.alive);
      if (!result.ok) {
        set({
          lastMessage:
            result.reason === 'not-connected'
              ? 'Không có lối đi theo hướng đó.'
              : (MOVE_FAIL_MESSAGE[result.reason] ?? 'Không thể di chuyển.'),
        });
        return;
      }
      set(successfulMovePatch(dungeon.playerRoomId, result.floor));
      gameSignals.emit({ type: 'room-transition', roomId: result.floor.playerRoomId });
    },

    selectOption: (optionId) => {
      const state = get();
      if (!state.player.alive || state.mode.type === 'combat') {
        return;
      }

      const room = getCurrentRoom(state.dungeon);
      const option = room?.options.find((entry) => entry.id === optionId);
      if (!room || !option) {
        return;
      }
      if (room.isRequiredInteraction && room.isCleared && option.consequence.setCleared) {
        return;
      }

      if (option.consequence.nextFloor && !option.consequence.triggerCombat) {
        get().advanceFloor(option.consequence.nextFloor);
        return;
      }

      if (option.consequence.attemptFlee || option.action === 'ESCAPE') {
        const enemyKey = enemyKeyFromOption(option);
        const enemy = resolveEnemy(enemyKey);
        const flee = resolvePreCombatFlee(state.player.stats, enemy.name);

        if (flee.outcome === 'escaped') {
          const retreat = retreatToPrevious(state.dungeon, state.previousRoomId, room.id);
          set({
            dungeon: retreat.floor,
            previousRoomId: retreat.previousRoomId,
            mode: modeFromRoom(getCurrentRoom(retreat.floor)),
            combat: null,
            lastMessage: flee.message,
          });
          gameSignals.emit({ type: 'room-transition', roomId: retreat.floor.playerRoomId });
          return;
        }

        set({
          lastMessage: flee.message,
          ...buildCombatFromEnemy(
            room.id,
            enemyKey,
            flee.message,
            state.dungeon.floorNumber,
          ),
        });
        return;
      }

      const result = applyConsequence(
        {
          floor: state.dungeon,
          player: state.player,
          currentRoomId: room.id,
          previousRoomId: state.previousRoomId,
          action: option.action,
        },
        option.consequence,
      );

      if (result.failed) {
        set({ lastMessage: result.message });
      } else if (result.killed) {
        set({
          dungeon: result.floor,
          player: result.player,
          previousRoomId: result.previousRoomId,
          mode: { type: 'dead' },
          combat: null,
          lastMessage: result.message,
        });
      } else if (result.triggerCombat && result.enemyKey) {
        set({
          dungeon: result.floor,
          player: result.player,
          previousRoomId: result.previousRoomId,
          lastMessage: result.message,
          ...buildCombatFromEnemy(
            room.id,
            result.enemyKey,
            result.message,
            result.floor.floorNumber,
          ),
        });
      } else {
        set({
          dungeon: result.floor,
          player: result.player,
          previousRoomId: result.previousRoomId,
          mode: modeFromRoom(getCurrentRoom(result.floor)),
          combat: null,
          lastMessage: result.message,
        });
      }
    },

    returnToExploration: () => {
      const { dungeon, player } = get();
      if (player.alive) {
        set({ mode: modeFromRoom(getCurrentRoom(dungeon)), combat: null });
      }
    },

    advanceFloor: (floorNumber) => {
      const { player } = get();
      if (!player.alive) {
        return;
      }
      const dungeon = generateFloor({
        floorNumber,
        seed: Date.now() % 1_000_000,
      });
      set({
        dungeon,
        previousRoomId: null,
        combat: null,
        mode: modeFromRoom(getCurrentRoom(dungeon)),
        lastMessage: `Bạn bước vào ${dungeon.floorName}.`,
      });
    },
  };
}
